<template>
  <div
    v-if="!isLoading2"
    class="section"
  >
    <div class="columns is-centered">
      <div class="column is-11">
        <div class="columns">
          <div class="column has-text-centered">
            <h2 class="title is-2">
              Income Sources
              <tooltip
                v-if="incomeSourceTooltip"
                :content="incomeSourceTooltip[0].description"
                :tooltip-options="{
                  classes: ['large'],
                }"
              />
            </h2>
            <div class="subtitle is-2">
              <div v-html="incomeSourceText ? incomeSourceText[0].description : ''" />
            </div>
            <checkbox-adv
              id="nothing-to-report"
              v-model="nothingToReport"
              :value="true"
            >
              Check this box if you have no <strong>Income Sources</strong> to report.
            </checkbox-adv>
          </div>
        </div>

        <div
          v-if="!nothingToReport"
          class="section"
        >
          <validation-observer
            ref="form-income-sources"
            tag="form"
          >
            <div class="columns is-multiline">
              <div class="column is-4">
                <textbox-vee
                  id="source-name"
                  v-model="newItem.name"
                  placeholder="Income Source Name"
                  desc="Include City of Philadelphia"
                  rules="required"
                  required
                />
              </div>
              <div class="column is-2 is-relative">
                <tooltip
                  v-if="incomeSourceTooltip"
                  :content="incomeSourceTooltip[1].description"
                  text=""
                  icon-position="before"
                  :absolute-positioned="true"
                  absolute-position="top: -15px; right: 12px;"
                  :tooltip-options="{
                    classes: ['large'],
                  }"
                />
                <textbox-vee
                  id="source-amount"
                  v-model="newItem.amount"
                  placeholder="Amount"
                  type="number"
                  min="0"
                  step="0.01"
                  rules="decimal|required"
                  required
                  desc="eg. XXXX.XX"
                />
              </div>
              <div class="column is-6">
                <textbox-vee
                  id="source-nature-of-business"
                  v-model="newItem.natureofbusiness"
                  placeholder="Nature of Business"
                  rules="required"
                  required
                />
              </div>
              <div class="column is-4">
                <textbox
                  id="street-address"
                  v-model="newItem.streetAddress"
                  placeholder="Address"
                />
              </div>
              <div class="column is-3">
                <textbox
                  id="city"
                  v-model="newItem.city"
                  placeholder="City"
                />
              </div>
              <div class="column is-3">
                <dropdown
                  v-model="newItem.state"
                  :options="states"
                  placeholder="State"
                />
              </div>
              <div class="column is-2">
                <textbox-vee
                  id="zip"
                  v-model="newItem.zip"
                  placeholder="Zip Code"
                  rules="zipCode"
                />
              </div>
              <div class="column is-12 has-text-centered">
                <button
                  id="action-btn"
                  type="button"
                  class="button is-secondary"
                  @click.prevent="trigerNewItemAction"
                >
                  {{ tableAction }} Income Source
                </button>
              </div>
            </div>
          </validation-observer>
          <div
            v-if="items"
            class="columns"
          >
            <div class="column">
              <v-client-table
                ref="vc-table"
                class="filer-screens-table"
                :data="allItems"
                :columns="table"
                :options="tableOptions"
              >
                <div
                  slot="edit"
                  slot-scope="props"
                >
                  <button
                    :id="`edit-btn-${props.row.id}`"
                    type="button"
                    title="Edit"
                    aria-label="Edit item"
                    class="button is-small is-warning"
                    @click.prevent="editItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-pencil-alt"
                    />
                  </button>
                </div>
                <div
                  slot="delete"
                  slot-scope="props"
                >
                  <button
                    :id="`delete-btn-${props.row.id}`"
                    type="button"
                    title="Delete"
                    aria-label="Delete item"
                    class="button is-small is-danger"
                    @click.prevent="deleteItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-trash"
                    />
                  </button>
                </div>
                <div
                  slot="clone"
                  slot-scope="props"
                >
                  <button
                    :id="`clone-btn-${props.row.id}`"
                    type="button"
                    title="Clone"
                    aria-label="Clone item"
                    class="button is-small is-secondary"
                    @click.prevent="cloneItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-copy"
                    />
                  </button>
                </div>
              </v-client-table>
              <br>
              <div class="message is-info">
                <div class="message-body">
                  <div v-html="incomeSourceText ? incomeSourceText[1].description : ''" />
                </div>
              </div>
            </div>
          </div>
        </div>


        <div class="columns">
          <div class="column has-text-centered">
            <button
              id="save-btn"
              type="button"
              class="button is-primary"
              :class="isSaving ? 'is-loading' : ''"
              :disabled="isSaving"
              @click="saveAndContinueCheck('Income Sources')"
            >
              Save and Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { ClientTable } from 'vue-tables-2';
import { filerScreenHelpers } from '@/mixins/filerScreen';
import { controlValueHelpers } from '@/mixins/controlValue';
import CheckboxAdv from '@/components/phila-ui/CheckboxAdv';
import Tooltip from '@/components/phila-ui/Tooltip';
import { Textbox, Dropdown } from '@phila/phila-ui';
import { withValidation } from 'vee-validate';


const TextboxVee = withValidation(Textbox);

Vue.use(ClientTable, {}, false, 'bulma');

export default {
  name: 'IncomeSources',
  components: {
    CheckboxAdv,
    TextboxVee,
    Textbox,
    Dropdown,
    Tooltip,
  },
  mixins: [
    filerScreenHelpers,
    controlValueHelpers,
  ],
  data () {
    return {
      currentFiling: {},
      items: [],
      isSaving: false,
      newItem: {
        type: '',
        name: '',
        amount: '',
        incomeGain: '',
        natureOfBusiness: '',
        natureOfIncome: '',
        streetAddress: '',
        city: '',
        state: '',
        zip: '',
        person: '',
      },
      nothingToReport: false,
      table: [
        'amount',
        'name',
        'streetAddress',
        'edit',
        'clone',
        'delete',
      ],
      tableAction: 'Add',
      tableOptions: {
        columnsClasses: {
          'edit': 'actions has-text-centered',
          'delete': 'actions has-text-centered',
          'clone': 'actions has-text-centered',
        },
        pagination: {
          dropdown: false,
          edge: true,
        },
        sortable: [],
        perPage: 50,
        perPageValues: [],
        filterable: false,
        headings: {
          name: "Source Name",
          amount: "Amount ($) *",
          streetAddress: "Addresss",
        },
        texts: {
          noResults: 'No Income Sources entered',
        },
      },
    };
  },
  computed: {
    incomeTypes () {
      return this.controlValueDropdown('incometype');
    },
    incomeSourceText () {
      return this.$store.getters[ 'admin/activeControlValues' ]('incomeSourceText');
    },
    incomeSourceTooltip () {
      return this.$store.getters[ 'admin/activeControlValues' ]('incomeSourceTooltip');
    },
    states () {
      return this.controlValueDropdown('state');
    },
    familyRelationships () {
      return this.controlValueDropdown('familymember');
    },
  },
  async created () {
    await this.initialFetch();
    await this.initialAssign();
    this.$store.commit('loading', false);
  },
  methods: {
    async initialAssign () {
      this.currentFiling = this.getCurrentFiling();
      this.items = this.getCurrentIncomeSources();
      this.hasNothingToReport();
    },
    async initialFetch () {
      let promises = [];
      promises.push(this.$store.dispatch('admin/getControlValue', 'state'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'incomeSourceText'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'incomeSourceTooltip'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'incometype'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'familymember'));
      promises.push(this.$store.dispatch('incomeSources/fetchIncomeSources'));
      return Promise.all(promises);
    },
    hasNothingToReport () {
      if (this.items.length === 0 && this.currentFiling.incomeCompleted === 'Y') {
        this.nothingToReport = true;
      }
    },
    getCurrentIncomeSources () {
      return this.cloneObj(this.$store.state.incomeSources.incomeSources);
    },
    async trigerNewItemAction () {
      const itemRef = this.$refs['form-income-sources'];
      const isValid = await itemRef.validate();

      if (!isValid) {
        return false;
      }

      if (this.tableAction === 'Add') {
        this.addNewItem();
      }
      if (this.tableAction === 'Update') {
        this.updateItem();
      }
      itemRef.reset();
    },
    saveAndContinue () {
      let promises = [];

      const col = 'incomeCompleted';
      const endpoint = 'income-sources';

      let filing = {
        id: this.currentFiling.id,
      };

      filing[col] = this.currentFiling[col];

      //V-model.number won't work here, so we need to parse it ourselves
      this.items.forEach(item => {
        item.amount = parseFloat(item.amount);
      });

      let isDeletingAll = this.isDeletingAllItems();

      if (this.nothingToReport) {
        filing[col] = 'Y';
      } else {
        if (this.items.length === 0) {
          filing[col] = 'N';
        } else {
          promises.push(this.$store.dispatch('filing/saveFilingScreen', {
            items: this.items,
            endpoint,
          }));
          if (isDeletingAll === true) {
            filing[col] = 'N';
          } else {
            filing[col] = 'Y';
          }

        }
      }

      let self = this;

      promises.push(this.$store.dispatch('filing/saveFiling', filing).then(() => {
        if (filing[col] === 'Y' && this.nothingToReport === true) {

          self.items.forEach(item => {
            self.deleteItem(item.id);
          }, self);

          self.$store.dispatch('filing/saveFilingScreen', {
            items: self.items,
            endpoint,
          });

        }
      }));


      Promise.all(promises).then(() => {
        this.$router.push({ name: this.nextFilingScreen });
      });

    },
  },
};
</script>

<style scoped lang="scss">
  .radio-group-h {
    label {
      display: block;
    }
    .input-radio {
      display: inline-block;
    }
  }
</style>